// [S-091] TenantUsers — list + invite + role-change + remove.
//
// Reused by:
//   platform_admin → for any tenant via /admin/tenants/:tenantId/users
//   tenant_admin   → for own tenant via /admin/tenants/:tenantId/users
//
// Role gate is layered:
//   1. Route-level <RequireRole minRole="tenant_admin"> in App.tsx — keeps
//      tenant_user out entirely.
//   2. Server-side TenantService gates — tenant_admin of A can't list
//      Studio B; can't promote anyone to platform_admin.
//   3. UI gate — the role <select> hides the platform_admin option for
//      tenant_admin callers (don't show what the server will reject).
//
// Confirmation modals fire for the two destructive paths: demoting a
// tenant_admin to tenant_user, and removing (deactivating) a user.
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { composeInviteEmail } from "@csai/core/browser";
import { useAuth, useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import {
  Alert,
  Button,
  Dialog,
  LoadingState,
  PageHead,
  Pagination,
  Select,
  usePagination,
} from "../components";
import { getApiErrorMessage } from "../data/api";
import { useCustomRoles, type CustomRole } from "../data/rbacQuery";
import {
  useInviteTenantUser,
  useRemoveTenantUser,
  useResendTenantInvite,
  useTenantDetail,
  useTenantUsersList,
  useUpdateTenantUserRole,
  type InviteTenantUserInput,
  type InviteTenantUserResponse,
  type TenantUserRow,
} from "../data/tenantsQuery";

// S-316 — a role-select value is either a built-in role name or a custom role
// encoded as `custom:<machine name>`. The two namespaces never collide because
// custom role names are validated to exclude the reserved built-in names.
const CUSTOM_ROLE_PREFIX = "custom:";

function customRoleValue(name: string): string {
  return `${CUSTOM_ROLE_PREFIX}${name}`;
}

// Maps the loaded custom roles into Select options (appended after built-ins).
// Empty `customRoles` yields [] so the selects render exactly today's
// built-in-only options — no regression when an organization has no custom roles.
function customRoleOptions(
  customRoles: readonly CustomRole[]
): ReadonlyArray<{ value: string; label: string }> {
  return customRoles.map((r) => ({
    value: customRoleValue(r.name),
    label: `${r.displayName} (custom)`,
  }));
}

type ConfirmState =
  | { kind: "idle" }
  | { kind: "remove"; user: TenantUserRow }
  | { kind: "demote"; user: TenantUserRow };

const ROLE_OPTIONS_PLATFORM_ADMIN = [
  { value: "platform_admin" as const, label: "platform_admin" },
  { value: "tenant_admin" as const, label: "Organization admin" },
  { value: "tenant_user" as const, label: "Organization user" },
];

const ROLE_OPTIONS_TENANT_ADMIN = [
  { value: "tenant_admin" as const, label: "Organization admin" },
  { value: "tenant_user" as const, label: "Organization user" },
];

function roleOptionsFor(
  canOfferPlatformAdmin: boolean
): ReadonlyArray<{ value: TenantUserRow["role"]; label: string }> {
  return canOfferPlatformAdmin
    ? ROLE_OPTIONS_PLATFORM_ADMIN
    : ROLE_OPTIONS_TENANT_ADMIN;
}

const INVITE_ROLE_OPTIONS = [
  { value: "tenant_user" as const, label: "Organization user" },
  { value: "tenant_admin" as const, label: "Organization admin" },
];

export function TenantUsers(): JSX.Element {
  const { tenantId: routeTenantId } = useParams<{ tenantId: string }>();
  const tenantId = routeTenantId ?? "";
  const session = useSession();
  // Only a platform principal (platform:manage_tenants over the platform/L1
  // scope) may offer to grant platform_admin or see the cross-tenant breadcrumb.
  const canOfferPlatformAdmin = sessionCan(session, "platform:manage_tenants", {
    layer: "L1",
  });
  const selfUid = useSelfUid();
  const { data, isLoading, isError, refetch, isFetching } =
    useTenantUsersList(tenantId);
  // Human-readable name for the tenant — never show the raw tenantId to
  // the user (breadcrumb, invite dialog, invite email all key off this).
  const studioName = useTenantDetail(tenantId).data?.studioId ?? "this organization";
  // S-316 — the organization's custom roles, used both to populate the role selects
  // and to look up the displayName for the effective-role label. `?? []` so a
  // still-loading or errored fetch degrades to built-in-only behavior.
  const customRoles = useCustomRoles(tenantId).data ?? [];
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>({ kind: "idle" });
  // S-295 — resend-invite + preview-email dialogs, keyed by the target user.
  const [resendUser, setResendUser] = useState<TenantUserRow | null>(null);
  const [previewUser, setPreviewUser] = useState<TenantUserRow | null>(null);
  // Hook before the loading/error guards (rules-of-hooks); seeded from the
  // (possibly undefined) data so it's called unconditionally every render.
  const pager = usePagination(data?.users ?? [], 25);
  const showLoading = isLoading || data === undefined;
  const activeRows = (data?.users ?? []).filter((r) => r.deactivatedAt === null);

  return (
    <>
      <PageHead
        eyebrow={
          <>
            {canOfferPlatformAdmin && (
              <>
                <Link to="/admin/tenants">Tenants</Link>{" "}
                <span style={{ color: "var(--cs-text-secondary)" }}>
                  · {studioName} ·{" "}
                </span>
              </>
            )}
            users
          </>
        }
        title={
          <>
            The <em>team</em> behind the studio.
          </>
        }
        sub={
          showLoading
            ? `Fetching users for ${studioName}.`
            : isError
              ? "The admin API responded with an error. Try again in a moment."
              : "Invite users, promote a peer to tenant_admin, or deactivate someone who's moved on. Every change writes a role-change audit event."
        }
        cta={
          !showLoading && !isError ? (
            <>
              <span className="reatt__head__refresh">
                {activeRows.length} active
              </span>
              <Button
                variant="secondary"
                className="btn--s"
                onClick={() => {
                  void refetch();
                }}
                disabled={isFetching}
                data-testid="tnt-users-refresh"
              >
                {isFetching ? "Refreshing…" : "Refresh"}
              </Button>
              <Button
                variant="primary"
                className="btn--s"
                onClick={() => setInviteOpen(true)}
                data-testid="tnt-users-invite-open"
              >
                Invite user
              </Button>
            </>
          ) : undefined
        }
      />

      {showLoading && <LoadingState label="Loading users…" />}

      {!showLoading && isError && (
        <Alert label="Users unavailable" tone="error">
          The admin API responded with an error. Try again in a moment.
        </Alert>
      )}

      {!showLoading && !isError && (
        <>
          {!isFetching && data.users.length === 0 ? (
            <EmptyState onInvite={() => setInviteOpen(true)} />
          ) : (
            <div className="tbl tbl--audit tbl--users" data-testid="tnt-users-table">
              <div className="tbl__row tbl__row--head">
                <span>Email</span>
                <span title="Role within this organization — distinct from a platform-wide role">
                  Tenant role
                </span>
                <span>Tier</span>
                <span>Joined</span>
                <span>Actions</span>
              </div>
              {pager.pageItems.map((row) => (
                <UserRowView
                  key={row.uid}
                  row={row}
                  tenantId={tenantId}
                  canOfferPlatformAdmin={canOfferPlatformAdmin}
                  customRoles={customRoles}
                  isSelf={row.uid === selfUid}
                  onRemove={() => setConfirm({ kind: "remove", user: row })}
                  onDemote={() => setConfirm({ kind: "demote", user: row })}
                  onResend={() => setResendUser(row)}
                  onPreview={() => setPreviewUser(row)}
                />
              ))}
            </div>
          )}
          <Pagination
            page={pager.page}
            pageCount={pager.pageCount}
            onPageChange={pager.setPage}
            total={pager.total}
            rangeStart={pager.rangeStart}
            rangeEnd={pager.rangeEnd}
            itemLabel="user"
          />
        </>
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        tenantId={tenantId}
        customRoles={customRoles}
      />
      <ConfirmDialog
        state={confirm}
        onClose={() => setConfirm({ kind: "idle" })}
        tenantId={tenantId}
      />
      {resendUser !== null && (
        <ResendInviteDialog
          user={resendUser}
          tenantId={tenantId}
          onClose={() => setResendUser(null)}
        />
      )}
      {previewUser !== null && (
        <PreviewEmailDialog
          user={previewUser}
          tenantId={tenantId}
          onClose={() => setPreviewUser(null)}
        />
      )}
    </>
  );
}

// The viewer's own IdP uid, so the row representing the caller can be
// marked. The role shown per row is the user's membership role *in this
// tenant*, which can differ from the caller's platform-wide role (a
// platform_admin can hold a tenant_admin membership here) — the "you"
// badge keeps that from reading as a contradiction.
function useSelfUid(): string | null {
  const { state } = useAuth();
  return state.status === "authenticated" ? state.session.uid : null;
}

function UserRowView({
  row,
  tenantId,
  canOfferPlatformAdmin,
  customRoles,
  isSelf,
  onRemove,
  onDemote,
  onResend,
  onPreview,
}: {
  row: TenantUserRow;
  tenantId: string;
  canOfferPlatformAdmin: boolean;
  customRoles: readonly CustomRole[];
  isSelf: boolean;
  onRemove: () => void;
  onDemote: () => void;
  onResend: () => void;
  onPreview: () => void;
}): JSX.Element {
  const update = useUpdateTenantUserRole(tenantId);
  const deactivated = row.deactivatedAt !== null;

  // S-316 — built-in role options PLUS the organization's custom roles. The current
  // value reflects the member's EFFECTIVE role: `custom:<name>` when a custom
  // role is assigned, else the bare built-in role.
  const roleOptions = [
    ...roleOptionsFor(canOfferPlatformAdmin),
    ...customRoleOptions(customRoles),
  ];
  const currentValue =
    row.assignedRoleName != null
      ? customRoleValue(row.assignedRoleName)
      : row.role;

  // S-316a — role changes STAGE locally and commit on an explicit Save, rather
  // than firing the moment the <select> changes. Fixes two things Bill's
  // feedback surfaced: (1) there was no Save affordance or success feedback, so
  // an already-persisted change read as "it won't save"; (2) a stray select
  // silently granted privileges (only the demote path was ever confirmed).
  // `pending` holds the staged select value; it is "active" only while it
  // differs from the persisted currentValue, so a successful save — which
  // refetches and moves currentValue — auto-clears the affordance without any
  // cross-component bookkeeping (including the demote-confirm path below).
  const [pending, setPending] = useState<string | null>(null);
  const selectValue = pending ?? currentValue;
  const hasPending = pending !== null && pending !== currentValue;

  const onSelect = (next: string): void => {
    update.reset(); // drop a prior error once the admin re-picks
    setPending(next === currentValue ? null : next);
  };

  const onCancel = (): void => {
    update.reset();
    setPending(null);
  };

  const onSave = (): void => {
    if (!hasPending || pending === null) return;
    // Switching to a custom role: send customRoleName; the backend floors the
    // base role to tenant_user (the custom capability set is the real grant).
    if (pending.startsWith(CUSTOM_ROLE_PREFIX)) {
      update.mutate(
        {
          uid: row.uid,
          role: "tenant_user",
          customRoleName: pending.slice(CUSTOM_ROLE_PREFIX.length),
        },
        { onSuccess: () => setPending(null) }
      );
      return;
    }
    const nextRole = pending as TenantUserRow["role"];
    // Demoting a tenant_admin is destructive — route Save through the confirm
    // dialog (which owns its own mutate + audit copy). The pending affordance
    // clears itself once the refetch moves currentValue to tenant_user.
    if (row.role === "tenant_admin" && nextRole === "tenant_user") {
      onDemote();
      return;
    }
    update.mutate(
      { uid: row.uid, role: nextRole },
      { onSuccess: () => setPending(null) }
    );
  };

  return (
    <div className="tbl__row" data-testid={`tnt-user-row-${row.uid}`}>
      <div className="tnt__email">
        <span className="src-edge src-edge--stud" aria-hidden="true" />
        <span>
          {row.email}
          {isSelf && <span className="tnt__self">you</span>}
          {deactivated && (
            <em className="tnt__deactivated">· deactivated</em>
          )}
          <InviteStatusBadge
            status={row.lastInviteStatus}
            sentAt={row.lastInviteSentAt}
          />
        </span>
      </div>
      <span>
        {/* tenant_admin callers never get the platform_admin option —
            even though the server would reject it, hiding the option
            keeps the UI honest. */}
        <Select<string>
          label={`Role for ${row.email}`}
          value={selectValue}
          options={roleOptions}
          onValueChange={(v) => {
            if (v !== undefined) onSelect(v);
          }}
          clearable={false}
          disabled={deactivated || update.isPending}
          triggerClassName={`tnt__role tnt__role--${row.role}`}
        />
        {hasPending && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 6,
            }}
            data-testid={`tnt-user-role-save-${row.uid}`}
          >
            <span style={{ color: "var(--cs-text-secondary)" }}>· changed</span>
            <Button
              variant="ghost"
              className="btn--s"
              type="button"
              onClick={onCancel}
              disabled={update.isPending}
              data-testid={`tnt-user-role-cancel-${row.uid}`}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn--s"
              type="button"
              onClick={onSave}
              loading={update.isPending}
              disabled={update.isPending}
              data-testid={`tnt-user-role-save-btn-${row.uid}`}
            >
              {update.isPending ? "Saving…" : "Save role"}
            </Button>
          </div>
        )}
        {update.isError && (
          <div
            className="tnt__form__error"
            role="alert"
            style={{ marginTop: 6 }}
            data-testid={`tnt-user-role-error-${row.uid}`}
          >
            {getApiErrorMessage(update.error) ??
              "Couldn't save the role. Try again."}
          </div>
        )}
      </span>
      <span>
        <span className={`tnt__tier tnt__tier--${row.accessTier}`}>
          {row.accessTier}
        </span>
      </span>
      <span className="tnt__when">{formatShortDate(row.createdAt)}</span>
      <span className="tnt__actions">
        <Button
          variant="ghost"
          className="btn--s"
          onClick={onPreview}
          data-testid={`tnt-user-preview-${row.uid}`}
        >
          Preview email
        </Button>
        <Button
          variant="ghost"
          className="btn--s"
          onClick={onResend}
          disabled={deactivated}
          data-testid={`tnt-user-resend-${row.uid}`}
        >
          Resend
        </Button>
        <Button
          variant="ghost"
          className="btn--s"
          onClick={onRemove}
          disabled={deactivated}
          data-testid={`tnt-user-remove-${row.uid}`}
        >
          {deactivated ? "Removed" : "Remove"}
        </Button>
      </span>
    </div>
  );
}

function EmptyState({ onInvite }: { onInvite: () => void }): JSX.Element {
  return (
    <div className="tnt__empty" data-testid="tnt-users-empty">
      <b>No users yet</b>
      Invite the first team member to get this organization started.
      <div style={{ marginTop: 12 }}>
        <Button
          variant="primary"
          className="btn--s"
          onClick={onInvite}
          data-testid="tnt-users-empty-invite"
        >
          Invite user
        </Button>
      </div>
    </div>
  );
}

function InviteDialog({
  open,
  onClose,
  tenantId,
  customRoles,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  customRoles: readonly CustomRole[];
}): JSX.Element | null {
  const mutation = useInviteTenantUser(tenantId);
  const [email, setEmail] = useState("");
  // S-316 — the chosen role value is a built-in role name OR `custom:<name>`.
  // Held as a plain string so the same Select can offer both.
  const [role, setRole] = useState<string>("tenant_user");
  // S-091a — successful invite returns a one-time first-time sign-in
  // link. The dialog flips into a confirmation panel that shows the
  // link with a copy-to-clipboard affordance. Closing the dialog
  // discards `success` so re-opening starts fresh — the link is shown
  // exactly once.
  const [success, setSuccess] = useState<InviteTenantUserResponse | null>(
    null
  );
  const studioName = useTenantDetail(tenantId).data?.studioId ?? "this organization";

  const reset = (): void => {
    setEmail("");
    setRole("tenant_user");
    setSuccess(null);
    mutation.reset();
  };

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const submitDisabled = mutation.isPending || email.trim().length === 0;

  // Surface the chosen role at the moment of action so a wrong role can't slip
  // through (the "added as a user by mistake" case). The button states the role
  // and the summary spells out who gets what — and reminds the admin roles are
  // editable inline, so there's no need to remove + re-add to fix one.
  const allRoleOptions = [
    ...INVITE_ROLE_OPTIONS,
    ...customRoleOptions(customRoles),
  ];
  const selectedLabel =
    allRoleOptions.find((o) => o.value === role)?.label ?? role;
  const roleShort =
    role === "tenant_admin"
      ? "admin"
      : role === "tenant_user"
        ? "member"
        : selectedLabel.replace(/ \(custom\)$/, "");
  const roleNoun =
    role === "tenant_admin"
      ? "an admin — full tenant management"
      : role === "tenant_user"
        ? "a member — workspace access only"
        : `${roleShort} (custom role)`;

  if (!open) return null;

  if (success !== null) {
    const emailed = success.inviteEmail?.delivered === true;
    const emailFailed = success.inviteEmail?.delivered === false;
    return (
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleClose();
        }}
        title={
          <span>
            {emailed
              ? `Invite emailed to ${success.email}`
              : "Invite sent — share the sign-in link"}
          </span>
        }
        description={
          <span>
            {emailed ? (
              <>
                We emailed the first-time sign-in link to {success.email}. The
                link below is a backup you can send another way if needed.
              </>
            ) : emailFailed ? (
              <>
                {success.email} was created, but the invite email
                didn&apos;t send
                {success.inviteEmail?.error
                  ? ` (${success.inviteEmail.error})`
                  : ""}
                . Copy the link below and send it manually.
              </>
            ) : (
              <>
                {success.email} can use this link to set a password and sign
                in. Copy and send it via your preferred channel.
              </>
            )}
          </span>
        }
      >
        <InviteSuccessPanel response={success} onClose={handleClose} />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title={<span>Invite a user to {studioName}</span>}
      description={
        <span>
          Creates an Identity Platform user under this organization, mints a
          one-time first-time sign-in link, and emits an audit event.
        </span>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          // S-316 — a `custom:<name>` selection sends customRoleName (and omits
          // a real built-in role; the backend floors it to tenant_user). A
          // built-in selection sends `role` exactly as before.
          const input: InviteTenantUserInput = role.startsWith(
            CUSTOM_ROLE_PREFIX
          )
            ? {
                email: email.trim(),
                role: "tenant_user",
                customRoleName: role.slice(CUSTOM_ROLE_PREFIX.length),
              }
            : {
                email: email.trim(),
                role: role as InviteTenantUserInput["role"],
              };
          mutation.mutate(
            input,
            {
              onSuccess: (response) => {
                // S-091a — flip into the confirmation panel rather than
                // close. If the admin API returned no link (no IDP wired),
                // close immediately like before.
                if (response.inviteLink !== null) {
                  setSuccess(response);
                } else {
                  handleClose();
                }
              },
            }
          );
        }}
      >
        <div className="tnt__form__row">
          <label htmlFor="tnt-invite-email">Email</label>
          <input
            id="tnt-invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="alex@studio.example"
            required
            data-testid="tnt-invite-email"
          />
        </div>
        <div className="tnt__form__row">
          <label>Role</label>
          <Select<string>
            label="Role"
            value={role}
            options={[...INVITE_ROLE_OPTIONS, ...customRoleOptions(customRoles)]}
            onValueChange={(v) => {
              if (v !== undefined) setRole(v);
            }}
            clearable={false}
          />
        </div>

        <div className="tnt__form__hint" data-testid="tnt-invite-summary">
          Adds{" "}
          <strong>{email.trim() === "" ? "this person" : email.trim()}</strong>{" "}
          to <strong>{studioName}</strong> as <strong>{roleNoun}</strong>. You can
          change someone&apos;s role anytime from the list — no need to remove
          and re-add them.
        </div>

        {mutation.isError && (
          <div className="tnt__form__error" role="alert" data-testid="tnt-invite-error">
            {getApiErrorMessage(mutation.error) ??
              "Could not invite the user. Check the email and try again."}
          </div>
        )}

        <div className="tnt__form__ft">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={mutation.isPending}
            type="button"
            data-testid="tnt-invite-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={submitDisabled}
            loading={mutation.isPending}
            data-testid="tnt-invite-submit"
          >
            {mutation.isPending ? "Inviting…" : `Invite as ${roleShort}`}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * S-091a — confirmation panel surfaced after a successful invite. The
 * link is shown in a read-only field; the copy button writes it to the
 * clipboard and surfaces a transient "Copied" state. The link is held
 * only in this component's local state — closing the dialog discards
 * it.
 */
function InviteSuccessPanel({
  response,
  onClose,
}: {
  response: InviteTenantUserResponse;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const link = response.inviteLink ?? "";
  const expiresAt = response.inviteLinkExpiresAt;

  const copy = async (): Promise<void> => {
    if (typeof navigator === "undefined" || navigator.clipboard === undefined) {
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Older browsers / restricted contexts: leave the field selectable
      // so the admin can copy manually.
    }
  };

  const isEmulatorLink = looksLikeEmulatorLink(link);

  return (
    <div data-testid="tnt-invite-success">
      {isEmulatorLink && (
        <div
          className="tnt__form__warning"
          role="alert"
          data-testid="tnt-invite-link-emulator"
        >
          <strong>This is a Firebase Auth emulator link.</strong> It only
          works from the machine running the emulator (the dev VM) and
          carries fake identity material. <em>Do not share externally.</em>{" "}
          To produce a real first-time-sign-in link, unset{" "}
          <code>FIREBASE_AUTH_EMULATOR_HOST</code> on the admin process so
          the Firebase Admin SDK targets the real Identity Platform
          project.
        </div>
      )}
      <div className="tnt__form__row">
        <label htmlFor="tnt-invite-link">First-time sign-in link</label>
        <input
          id="tnt-invite-link"
          type="text"
          value={link}
          readOnly
          onFocus={(e) => e.currentTarget.select()}
          data-testid="tnt-invite-link"
        />
      </div>
      {expiresAt !== null && (
        <div className="tnt__form__hint" data-testid="tnt-invite-link-expires">
          Valid until {new Date(expiresAt).toLocaleString()} — share it
          before then.
        </div>
      )}
      <div className="tnt__form__ft">
        <Button
          variant="secondary"
          type="button"
          onClick={() => {
            void copy();
          }}
          data-testid="tnt-invite-copy"
        >
          {copied ? "Copied" : "Copy link"}
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={onClose}
          data-testid="tnt-invite-success-close"
        >
          Done
        </Button>
      </div>
    </div>
  );
}

function ConfirmDialog({
  state,
  onClose,
  tenantId,
}: {
  state: ConfirmState;
  onClose: () => void;
  tenantId: string;
}): JSX.Element | null {
  const remove = useRemoveTenantUser(tenantId);
  const update = useUpdateTenantUserRole(tenantId);
  if (state.kind === "idle") return null;
  const isRemove = state.kind === "remove";
  const mutation = isRemove ? remove : update;

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      title={<span>{isRemove ? "Remove user" : "Demote tenant_admin"}</span>}
      description={
        <span>
          {isRemove
            ? `Deactivate ${state.user.email}? They will lose access immediately. Audit logs are preserved.`
            : `Change ${state.user.email} from tenant_admin to tenant_user? They will lose admin privileges for this organization.`}
        </span>
      }
    >
      {mutation.isError && (
        <div className="tnt__form__error" role="alert" data-testid="tnt-confirm-error">
          {getApiErrorMessage(mutation.error) ?? "The action did not complete. Try again."}
        </div>
      )}
      <div className="tnt__form__ft">
        <Button
          variant="ghost"
          onClick={onClose}
          disabled={mutation.isPending}
          type="button"
          data-testid="tnt-confirm-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          disabled={mutation.isPending}
          onClick={() => {
            if (isRemove) {
              remove.mutate(state.user.uid, { onSuccess: onClose });
            } else {
              update.mutate(
                { uid: state.user.uid, role: "tenant_user" },
                { onSuccess: onClose }
              );
            }
          }}
          data-testid="tnt-confirm-submit"
        >
          {mutation.isPending
            ? "Working…"
            : isRemove
              ? "Remove"
              : "Demote"}
        </Button>
      </div>
    </Dialog>
  );
}

// S-295 — inline "last invited" indicator shown under the email. Renders
// nothing for users who've never been invited (status null).
function InviteStatusBadge({
  status,
  sentAt,
}: {
  status: TenantUserRow["lastInviteStatus"];
  sentAt: string | null;
}): JSX.Element | null {
  if (status === null) return null;
  const when = sentAt !== null ? relativeTime(sentAt) : null;
  const label =
    status === "emailed"
      ? `Emailed${when !== null ? ` ${when}` : ""}`
      : status === "email_failed"
        ? "Email failed"
        : "Link only";
  return (
    <em
      className={`tnt__invite tnt__invite--${status}`}
      data-testid="tnt-user-invite-status"
      title={
        sentAt !== null
          ? `Invite last sent ${new Date(sentAt).toLocaleString()}`
          : undefined
      }
    >
      {" · "}
      {label}
    </em>
  );
}

// S-295 — confirm + re-mint a fresh sign-in link and (re)send the email,
// reusing the post-invite success panel (link + copy + email status).
function ResendInviteDialog({
  user,
  tenantId,
  onClose,
}: {
  user: TenantUserRow;
  tenantId: string;
  onClose: () => void;
}): JSX.Element {
  const mutation = useResendTenantInvite(tenantId);
  const result = mutation.data ?? null;
  const emailed = result?.inviteEmail?.delivered === true;

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      title={
        <span>
          {result === null
            ? `Resend invite to ${user.email}`
            : emailed
              ? `Invite re-sent to ${user.email}`
              : "New sign-in link ready"}
        </span>
      }
      description={
        <span>
          {result === null
            ? "This mints a brand-new one-time sign-in link (the previous one stops working) and emails it if email delivery is enabled."
            : emailed
              ? "We emailed a fresh sign-in link. The link below is a backup you can send another way."
              : "A fresh sign-in link was minted. Copy it below and send it to the user."}
        </span>
      }
    >
      {result === null ? (
        <div data-testid="tnt-resend-confirm-panel">
          {mutation.isError && (
            <div className="tnt__form__warning" role="alert">
              {getApiErrorMessage(mutation.error) ??
                "Resend failed. Try again."}
            </div>
          )}
          <div className="tnt__form__ft">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ uid: user.uid })}
              data-testid="tnt-resend-confirm"
            >
              {mutation.isPending ? "Sending…" : "Resend invite"}
            </Button>
          </div>
        </div>
      ) : (
        <InviteSuccessPanel response={result} onClose={onClose} />
      )}
    </Dialog>
  );
}

// S-295 — renders the exact invite email (same composeInviteEmail template
// the recipient gets) with the one-time link shown as a placeholder. The
// real link is never available here — preview is content-only.
function PreviewEmailDialog({
  user,
  tenantId,
  onClose,
}: {
  user: TenantUserRow;
  tenantId: string;
  onClose: () => void;
}): JSX.Element {
  const role = user.role === "tenant_user" ? "tenant_user" : "tenant_admin";
  const studioName = useTenantDetail(tenantId).data?.studioId ?? "this organization";
  const message = composeInviteEmail({
    to: user.email,
    inviteLink:
      "https://…  one-time sign-in link (sent only in the real email)  …",
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
    workspaceLabel: studioName,
    role,
  });
  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      title={<span>Invite email preview</span>}
      description={
        <span>
          What {user.email} receives. The one-time sign-in link is shown as a
          placeholder — it&apos;s only ever included in the email actually
          sent.
        </span>
      }
    >
      <div data-testid="tnt-email-preview">
        <div className="tnt__form__hint">
          <strong>Subject:</strong> {message.subject}
        </div>
        <iframe
          title="Invite email preview"
          srcDoc={message.html}
          sandbox=""
          style={{
            width: "100%",
            height: 360,
            border: "1px solid var(--cs-border-subtle)",
            borderRadius: 8,
            marginTop: 8,
          }}
        />
      </div>
      <div className="tnt__form__ft">
        <Button variant="primary" type="button" onClick={onClose}>
          Close
        </Button>
      </div>
    </Dialog>
  );
}

// S-295 — coarse relative time for the invite badge ("2h ago"). Coarse on
// purpose: the exact timestamp is in the badge's title attribute.
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// When `FIREBASE_AUTH_EMULATOR_HOST` is set in the admin process env,
// the Firebase Admin SDK's `generatePasswordResetLink` returns an
// emulator URL of the shape
// `http://127.0.0.1:9099/emulator/action?...&apiKey=fake-api-key`.
// That link only resolves on the dev VM and uses fake identity
// material, so it is not shareable. We warn the admin instead of
// silently presenting it as a normal invite link.
function looksLikeEmulatorLink(link: string): boolean {
  if (link.length === 0) return false;
  return (
    /\/emulator\/action(\?|$)/i.test(link) ||
    /\bapiKey=fake-api-key\b/i.test(link) ||
    /^https?:\/\/(127\.0\.0\.1|localhost|0\.0\.0\.0)(:\d+)?\//i.test(link)
  );
}
